import React, {useEffect, useState} from "react";
import PageLoadingSpinner from "../../PageLoadingSpinner";
import EmptyPeriodState from "../../EmptyPeriodState";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {getReport} from "../../../services/Strategy";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {currencyFormat} from "../../../utils/masks";
import {useTranslation} from "react-i18next";
import {StrategyNameCell} from "../../../pages/Strategy/styles";
import {
    StrategyReportTableContainer,
    OpsPrimary,
    OpsMeta,
} from "./styles";

function entryWord(count, t) {
    const n = parseInt(count, 10) || 0;
    return n === 1 ? t('strategy_report_entry_singular') : t('strategy_report_entry_plural');
}

function renderProfit(value, coinType) {
    const n = parseFloat(value);
    const text = `${coinType} ${currencyFormat(n)}`;
    if (n > 0) {
        return <span style={{color: '#5bd37c'}}>{text}</span>;
    }
    if (n < 0) {
        return <span style={{color: '#ff8a8a'}}>{text}</span>;
    }
    return <span style={{color: 'rgba(224, 224, 224, 0.95)'}}>{text}</span>;
}

export default function StrategyReport({filter, ...props}) {
    const {isActiveSignature} = useSelector(
        (state) => state.UserReducer
    );
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const {coinType} = useSelector((state) => state.BankReducer);
    const { t } = useTranslation();

    useEffect(() => {

        setLoading(true);
        getReport({
            startDate: filter[0].startDate,
            endDate: filter[0].endDate,
            direction: filter[0].direction,
        }).then((data) => {
            if (!data || data === '' || !data.data) {
                setList([]);
                setLoading(false);
                return;
            }

            setList(data.data.data);
            setLoading(false);
        }).catch(() => {
            setList([]);
            setLoading(false);
        });

        return function cleanup() {
            setList([]);
        };
    }, [filter]);

    return (
        <div {...props} style={{width: '100%', maxWidth: '100%', minWidth: 0}}>
            {isActiveSignature ?
                <div className="col-md-12 p-0 w-100">
                    {loading ?
                        <PageLoadingSpinner minHeight={180} padding="32px 20px"/> :
                        <>
                            {list && list.length ?
                                <>
                                {/* Mobile: cards */}
                                <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, pt: 1.5, pb: 1, px: 1.5 }}>
                                    {list.map((row, index) => {
                                        const total = parseInt(row.qtdeLoss, 10) + parseInt(row.qtdeWin, 10) + parseInt(row.qtdeDraw, 10);
                                        const pct = total ? parseFloat((row.qtdeWin * 100) / total).toFixed(2) : '';
                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    borderRadius: '10px',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    background: 'rgba(255,255,255,0.04)',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {/* Nome + assertividade */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1.5, pb: 1, gap: 1 }}>
                                                    <span style={{ fontWeight: 600, color: '#e0e0e0', fontSize: '0.95rem', flex: 1 }}>
                                                        {row.nameStrategy}
                                                    </span>
                                                    {total ? (
                                                        <span style={{ fontWeight: 700, color: '#f29205', fontSize: '0.9rem', flexShrink: 0 }}>
                                                            {pct}%
                                                        </span>
                                                    ) : null}
                                                </Box>

                                                {/* Operações + resultado */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', px: 2, pb: 1.5, gap: 1 }}>
                                                    <Box>
                                                        <OpsPrimary style={{ marginBottom: 2 }}>
                                                            {row.count} {entryWord(row.count, t)}
                                                        </OpsPrimary>
                                                        <OpsMeta>
                                                            {row.qtdeWin} {t('victories')} · {row.qtdeLoss} {t('defeats')} · {row.qtdeDraw} {t('draws')}
                                                        </OpsMeta>
                                                    </Box>
                                                    <span style={{ fontWeight: 700, fontSize: '0.95rem', flexShrink: 0 }}>
                                                        {renderProfit(row.profitObtained, coinType)}
                                                    </span>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Desktop: tabela */}
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    <StrategyReportTableContainer>
                                        <Table aria-label={t('report_strategies_title')}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('strategy_report_col_strategy')}</TableCell>
                                                    <TableCell align="center">{t('operations')}</TableCell>
                                                    <TableCell align="center">{t('assertiveness')}</TableCell>
                                                    <TableCell align="right">{t('profit_prejudice')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {list.map((row, index) => {
                                                    const total = parseInt(row.qtdeLoss, 10) + parseInt(row.qtdeWin, 10) + parseInt(row.qtdeDraw, 10);
                                                    const pct = total
                                                        ? parseFloat((row.qtdeWin * 100) / total).toFixed(2)
                                                        : '';
                                                    return (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <StrategyNameCell>{row.nameStrategy}</StrategyNameCell>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <OpsPrimary>{row.count} {entryWord(row.count, t)}</OpsPrimary>
                                                                <OpsMeta>
                                                                    {row.qtdeWin} {t('victories')} · {row.qtdeLoss} {t('defeats')} · {row.qtdeDraw} {t('draws')}
                                                                </OpsMeta>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                                                {total ? `${pct}%` : '—'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                                                {renderProfit(row.profitObtained, coinType)}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </StrategyReportTableContainer>
                                </Box>
                                </> :
                                <EmptyPeriodState iconClassName="fas fa-layer-group"/>
                            }
                        </>
                    }
                </div>
                : <>
                    <div className="alert alert-primary text-center" role="alert">
                        {t('signature_text_acess_all_resources')}
                        <br/>
                        <Link className="btn btn-success btn-sm font-weight-bold mt-4" to="/plans">
                            {t('signature_btn')}
                        </Link>
                    </div>
                </>
            }
        </div>
    );
}
